// Vardiyalar tablosu için gruplama mantığı.
//
// Aktif Siparişler tablosundaki kurye bazlı gruplamanın (orderGrouping.js) proje bazlı karşılığı:
// dedike (+ isDedicatedPool) satırlar "Firma / Şube" grubuna, düz havuz satırları kendi havuz
// adıyla ayrı gruplara girer. Bu ayrım, önceki "Proje" kolonundaki mantığın (ActiveShiftsTable.js)
// birebir aynısıdır — kolon kalktığı için bilgi artık grup başlığında taşınır.

import moment from "moment";

/** Bir vardiya satırının başlangıç anını verir — sıralama tek bir zaman eksenine indirgenir. */
const resolveShiftStartTime = row => {
    if (row.shiftType === "dedicated") {
        return moment(row.raw?.shiftStartTime);
    }
    // Havuz vardiyasında saatler "HH:mm" metni (ActiveShiftsTable.js'teki shiftActionAvailability
    // ile aynı hesap, gece yarısını aşan aralık burada önemli değil — yalnız başlangıç anı lazım).
    return moment(`${row.raw?.date} ${row.raw?.plannedStartTime}`, "YYYY-MM-DD HH:mm");
};

const resolveGroupKey = row => {
    if (row.shiftType === "dedicated" || row.isDedicatedPool) {
        return {
            key: `project-${row.companyName || "-"}|${row.subCompanyName || "-"}`,
            title: row.companyName || "-",
            subTitle: row.subCompanyName || null
        };
    }
    return {
        key: `pool-${row.poolName || "-"}`,
        title: row.poolName || "-",
        subTitle: null
    };
};

/**
 * Vardiya satırlarını proje/havuz bazlı gruplara ayırıp tabloya basılacak düz diziye çevirir.
 * Her eleman ya bir grup başlığı (`_rowType: "group"`) ya da bir vardiya satırıdır
 * (`_rowType: "shift"`). Grup başlıkları antd Table'da colSpan ile tam genişlik render edilir.
 *
 * Sıralama iki seviyede de azalandır (desc): gruplar, içerdikleri EN GEÇ başlayacak vardiyaya
 * göre; grup içindeki satırlar da kendi başlangıç saatine göre azalan sıradadır.
 *
 * `statusFilter` verilirse (ve "all" değilse) yalnızca o duruma uyan satırlar kalır; `startTimeFilter`
 * verilirse (ve "all" değilse) yalnızca o saatte (`startLabel`, ör. "18:00") başlayan satırlar
 * kalır. Bir grubun tüm satırları elenirse grup başlığı da gösterilmez.
 */
export const buildShiftRows = ({rows = [], statusFilter = "all", startTimeFilter = "all"} = {}) => {
    const filteredRows = rows
        .filter(row => !statusFilter || statusFilter === "all" || row.status === statusFilter)
        .filter(
            row =>
                !startTimeFilter || startTimeFilter === "all" || row.startLabel === startTimeFilter
        );

    const groups = new Map();
    filteredRows.forEach(row => {
        const {key, title, subTitle} = resolveGroupKey(row);
        if (!groups.has(key)) {
            groups.set(key, {key, title, subTitle, rows: []});
        }
        groups.get(key).rows.push(row);
    });

    const groupList = Array.from(groups.values()).map(group => {
        const sortedRows = [...group.rows].sort((a, b) => {
            const diff = resolveShiftStartTime(b).valueOf() - resolveShiftStartTime(a).valueOf();
            if (diff !== 0) return diff;
            return (a.courierName || "").localeCompare(b.courierName || "", "tr-TR");
        });
        const latestStartTime = sortedRows.length
            ? resolveShiftStartTime(sortedRows[0]).valueOf()
            : 0;
        return {...group, rows: sortedRows, latestStartTime};
    });

    groupList.sort((a, b) => b.latestStartTime - a.latestStartTime);

    const flatRows = [];
    groupList.forEach(group => {
        flatRows.push({
            _rowType: "group",
            key: `group-${group.key}`,
            title: group.title,
            subTitle: group.subTitle,
            count: group.rows.length
        });
        group.rows.forEach(row => flatRows.push({...row, _rowType: "shift"}));
    });

    return flatRows;
};
